
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

interface EditJobDescriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string;
  roleTitle: string;
  onSave: () => void;
}

interface RoleDescriptionData {
  text?: string;
  pdf_url?: string;
  pdf_filename?: string;
}

export function EditJobDescriptionDialog({
  open,
  onOpenChange,
  roleId,
  roleTitle,
  onSave
}: EditJobDescriptionDialogProps) {
  const [roleData, setRoleData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editableText, setEditableText] = useState<string>("");

  useEffect(() => {
    if (open && roleId) {
      fetchRoleData();
    }
  }, [open, roleId]);

  const fetchRoleData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('role_jd')
        .select('role_title, role_description')
        .eq('id', roleId)
        .single();

      if (error) throw error;
      setRoleData(data);

      // Parse the role_description JSON properly
      let roleDescription: RoleDescriptionData = {};
      
      if (data?.role_description) {
        if (typeof data.role_description === 'string') {
          try {
            roleDescription = JSON.parse(data.role_description);
          } catch {
            roleDescription = { text: data.role_description };
          }
        } else if (typeof data.role_description === 'object' && data.role_description !== null) {
          roleDescription = data.role_description as RoleDescriptionData;
        }
      }

      // If there's a PDF URL, fetch the content from that URL
      if (roleDescription.pdf_url) {
        try {
          const response = await fetch(roleDescription.pdf_url);
          if (response.ok) {
            const textContent = await response.text();
            setEditableText(formatJobDescription(textContent));
          } else {
            console.error('Failed to fetch PDF content');
            setEditableText(formatJobDescription(roleDescription.text || ''));
          }
        } catch (fetchError) {
          console.error('Error fetching PDF content:', fetchError);
          setEditableText(formatJobDescription(roleDescription.text || ''));
        }
      } else {
        // Use the text content directly
        setEditableText(formatJobDescription(roleDescription.text || ''));
      }
    } catch (error) {
      console.error('Error fetching role data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatJobDescription = (description: string) => {
    if (!description) return '';
    // Replace €¢ with •
    return description.replace(/€¢/g, '•');
  };

  const handleSave = async () => {
    if (!roleId || !editableText.trim()) return;

    setSaving(true);
    try {
      // Update the role_description with the new text
      const updatedRoleDescription = {
        text: editableText.trim()
      };

      const { error } = await supabase
        .from('role_jd')
        .update({
          role_description: updatedRoleDescription
        })
        .eq('id', roleId);

      if (error) throw error;

      onSave(); // Refresh the parent component
      onOpenChange(false); // Close the dialog
    } catch (error) {
      console.error('Error saving job description:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditableText(''); // Reset the text
    onOpenChange(false);
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] bg-secondary border-primary/20">
          <div className="flex items-center justify-center p-8">
            <div className="text-white">Loading...</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-secondary border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-primary">
            Edit Job Description - {roleTitle}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Textarea
              value={editableText}
              onChange={(e) => setEditableText(e.target.value)}
              className="h-[400px] w-full rounded-md border border-primary/20 bg-secondary/30 p-4 text-white text-[11px] resize-none"
              placeholder="Enter job description..."
            />
          </div>

          <DialogFooter className="flex justify-end gap-2 pt-4">
            <Button 
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
              className="px-8"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving || !editableText.trim()}
              className="bg-primary hover:bg-primary/90 text-black px-8"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
